import {
  Building,
  RoomResource,
  IsaveResource,
  PropertiesManagementLocation,
} from './resourceManagementTypes';
import {
  SET_LOCATION,
  SET_RESOURCE_BY_ID,
  setLocationActionType,
  ResourceManagementThunk,
  SetResourceByIdActionType,
  RESOURCE_MANAGEMENT_SET_SLOTS,
  RESOURCE_MANAGEMENT_SET_BUILDINGS,
  RESOURCE_MANAGEMENT_SET_RESOURCES,
  resourceManagementSetSlotsActionType,
  resourceManagementSetBuildingsActionType,
  resourceManagementSetResourcesActionType,
} from './resourceManagementActionTypes';
import { AUTH_NAMESPACE } from '../Auth/authReducer';
import { CREDITS_NAMESPACE } from '../credits/creditsReducer';
import RoomManagementApi from '../../API/room/RoomManagement.api';
import { MyActivitiesType } from '../my-activities/myActivitiesTypes';
import { startLoaderThunk, stopLoaderThunk } from '../modal/modalActions';
import { FetchMyActivitiesThunk } from '../my-activities/myActivitiesActions';
import { getUTCOffsetMs } from '../../utility/functions/utc-offset-ms.util';

export function resourceManagementSetSlotsAction(
  slots: number[]
): resourceManagementSetSlotsActionType {
  return {
    type: RESOURCE_MANAGEMENT_SET_SLOTS,
    payload: slots,
  };
}

export function resourceManagementSetResourcesAction(
  resources: RoomResource[]
): resourceManagementSetResourcesActionType {
  return {
    type: RESOURCE_MANAGEMENT_SET_RESOURCES,
    payload: resources,
  };
}

export function resourceManagementSetBuildingsAction(
  buildings: Building[]
): resourceManagementSetBuildingsActionType {
  return {
    type: RESOURCE_MANAGEMENT_SET_BUILDINGS,
    payload: buildings,
  };
}

export function setLocationAction(
  location: PropertiesManagementLocation
): setLocationActionType {
  return {
    type: SET_LOCATION,
    payload: location,
  };
}

export function setResourceByIdAction(
  resource: RoomResource
): SetResourceByIdActionType {
  return {
    type: SET_RESOURCE_BY_ID,
    payload: resource,
  };
}

export function resourceManagementFetchResourcesThunk(): ResourceManagementThunk<
  Promise<void>
> {
  return async (dispatch, getState) => {
    const profile = getState()[AUTH_NAMESPACE];

    dispatch(startLoaderThunk()).finally();
    RoomManagementApi.getResources(profile.profile?.locationId)
      .then((response) => {
        const resources = response.data.map((resource) => ({
          ...resource,
          availableFrom: Number(resource.availableFrom),
          availableTo: Number(resource.availableTo),
          maximumBookingTime: Number(resource.maximumBookingTime),
          minimumBookingTime: Number(resource.minimumBookingTime),
        }));
        dispatch(resourceManagementSetResourcesAction(resources));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function resourceManagementFetchResourceByIdThunk(
  id: number
): ResourceManagementThunk<Promise<any>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return RoomManagementApi.getResourceById(id)
      .then((response) => {
        return response.data;
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function resourceManagementSaveResource(
  data: IsaveResource
): ResourceManagementThunk<Promise<any>> {
  return async (dispatch, getState) => {
    const { AUTH_NAMESPACE } = getState();
    const { profile } = AUTH_NAMESPACE;
    const creditState = getState()[CREDITS_NAMESPACE];
    dispatch(startLoaderThunk()).finally();
    const allocation = {
      ...data,
      user_id: profile?.id,
      company_id: profile?.company?.id,
    };

    if (
      allocation.user_id &&
      !allocation.invited_users?.some((userId) => userId === allocation.user_id)
    ) {
      allocation.invited_users?.push(allocation.user_id);
    }

    const creditBalance = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      credit_amount: creditState.creditInUse?.creditsAmount || undefined,
      cash_amount: creditState.creditInUse?.cashAmount || undefined,
    };

    try {
      await RoomManagementApi.saveResourceAllocation(allocation, creditBalance);
      return {
        isSuccessfull: true,
        errorMessage: null,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error?.message || 'Unavailbale at this time';
      return {
        isSuccessfull: false,
        errorMessage,
      };
    } finally {
      dispatch(stopLoaderThunk()).finally();
    }
  };
}

export function resourceManagementFetchBuildingsThunk(
  scope?: number
): ResourceManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    const profile = getState()[AUTH_NAMESPACE].profile;
    dispatch(startLoaderThunk()).finally();
    RoomManagementApi.getAllBuildings(scope)
      .then((response) => {
        dispatch(resourceManagementSetBuildingsAction(response.data));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function RemoveBookedResourceThunk(
  activityId: number,
  callBack: (isSuccessful: boolean, s: string) => void
): ResourceManagementThunk<Promise<void>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    try {
      RoomManagementApi.cancelBooking(activityId).then((response) => {
        dispatch(FetchMyActivitiesThunk(MyActivitiesType.FUTURE, 12));
      });
    } catch (error) {
      // @ts-ignore
      callBack(false, error.message);
    } finally {
      dispatch(stopLoaderThunk());
    }
  };
}

export function ResourceManagementFetchSlotsThunk(
  id: number,
  startTime: number,
  alloc_id?: number
): ResourceManagementThunk<Promise<void>> {
  return async (dispatch) => {
    return RoomManagementApi.getResourceAvailableSlots(
      id,
      startTime + getUTCOffsetMs(),
      alloc_id
    ).then((response) => {
      dispatch(resourceManagementSetSlotsAction(response.data));
    });
  };
}

export function propertyManagementFetchLocationByIdThunk(
  id: number
): ResourceManagementThunk<Promise<any>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return RoomManagementApi.getLocationById(id)
      .then((response) => {
        dispatch(setLocationAction(response.data));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}

export function resourceManagementUpdateResource(
  data: IsaveResource
): ResourceManagementThunk<Promise<any>> {
  return async (dispatch, getState) => {
    const { AUTH_NAMESPACE } = getState();
    const { profile } = AUTH_NAMESPACE;
    const creditState = getState()[CREDITS_NAMESPACE];
    dispatch(startLoaderThunk()).finally();
    const allocation = {
      ...data,
      user_id: profile?.id,
      company_id: profile?.company?.id,
      resource_alloc_id: data.resource_alloc_id,
    };

    const creditBalance = {
      credit_payments: creditState.creditInUse?.activeCredits?.map((credit) => {
        return {
          credit_balance_id: credit.id,
          credit_amount: credit.amount,
        };
      }),
      credit_amount: creditState.creditInUse?.creditsAmount || undefined,
      cash_amount: creditState.creditInUse?.cashAmount || undefined,
    };

    return RoomManagementApi.updateResourceAllocation(allocation, creditBalance)
      .then(() => {
        dispatch(FetchMyActivitiesThunk(MyActivitiesType.FUTURE, 12));
      })
      .finally(() => {
        dispatch(stopLoaderThunk()).finally();
      });
  };
}

export function resourceManagementFetchValidResourceByIdThunk(
  id: number
): ResourceManagementThunk<Promise<any>> {
  return async (dispatch, getState) => {
    dispatch(startLoaderThunk()).finally();
    return RoomManagementApi.getValidResourceById(id)
      .then((response) => {
        dispatch(setResourceByIdAction(response.data));
      })
      .finally(() => {
        dispatch(stopLoaderThunk());
      });
  };
}
